import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';

// Components
import LabelsSearchBar from './components/LabelsSearchBar';
import LabelLists from './components/LabelLists';
import Button from '../../../../../components/buttons/Button';
import Icon from '../../../../../components/icons/Icon';

import { fetchLabelsFromDb } from '../../../../../element/labels/labelsService';
import { loadLabels } from '../../../../../../common/labels/labelActions';
import { useTranslator } from '../../../../../components/i18n/useTranslator';
import useLabelHandlers from './hooks/useLabelHandlers';
import { wrapWithLocaleDoubleQuotes } from '../../../../../../common/utils/i18n/quotationStyleUtils';

// Selectors
import { selectSelectedElementsNormalisedForTaskLists } from '../../../selector/selectionToolsSelector';
import { userLocalePreferenceSelector } from '../../../../../user/currentUserSelector';

// Types
import { LabelsToolContentProps } from './labelComponentPropTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

import './LabelsToolContent.scss';

const LabelsToolContent = (props: LabelsToolContentProps): ReactElement => {
    const { handleClickEdit, allLabels } = props;

    const dispatch = useDispatch();

    useEffect(() => {
        fetchLabelsFromDb().then((labels) => dispatch(loadLabels(labels)));
    }, []);

    const translate = useTranslator('popup', 'labels.create');

    const [searchString, setSearchString] = useState<string>('');
    const userLocalePreference = useSelector(userLocalePreferenceSelector);

    const elements: List<ImMNElement> = useSelector(selectSelectedElementsNormalisedForTaskLists);

    const { handleToggleLabelSelection, handleCreateLabel, handleSearchSubmit } = useLabelHandlers(
        props,
        searchString,
        setSearchString,
        elements,
        allLabels,
    );

    const showCreateButton = useMemo(
        () => searchString && !allLabels.some((label) => label.name === searchString),
        [searchString, allLabels],
    );
    return (
        <div className="LabelsToolContent">
            <LabelsSearchBar value={searchString} onChange={setSearchString} handleSearchSubmit={handleSearchSubmit} />
            <LabelLists
                handleToggleLabelSelection={handleToggleLabelSelection}
                handleClickEdit={handleClickEdit}
                elements={elements}
                searchString={searchString}
                allLabels={allLabels}
            />
            {showCreateButton && (
                <Button
                    className="new-label-button flex flex-row cursor-pointer mt-2"
                    onClick={() => handleCreateLabel()}
                >
                    <Icon name="plus" />
                    <span className={`text-left ml-1`}>
                        {translate('buttonText')}{' '}
                        <span className="font-bold">
                            {wrapWithLocaleDoubleQuotes(searchString, userLocalePreference)}
                        </span>
                    </span>
                </Button>
            )}
        </div>
    );
};

export default LabelsToolContent;
